import { mat3 } from 'gl-matrix';
import { toPng } from 'html-to-image';

const DATA_URL_REGEX = /^data:([a-zA-Z]+\/[a-zA-Z]+);([a-zA-Z0-9\-_]*),(.*)$/;

const getTextRegionPosition = (region, layout) => {
  const position = { x: 0, y: 0 };
  switch (layout.id) {
    case 1: // Text only
      position.x = 125;
      position.y = 100;
      return position;
    case 2: // Text left - Text right
      position.x = region.id === 'Text Region 1' ? 90 : 560;
      position.y = region.id === 'Text Region 1' ? 75 : 0;
      return position;
    case 3: // Image left - Text right
      position.x = 550;
      position.y = 5;
      return position;
    case 4: // Text left - image right
      position.x = -550;
      position.y = 5;
      return position;
    case 5: // Group Services Text Left - 1 region
      position.x = 100;
      position.y = 40;
      return position;
    case 6: // Group Services Text Right - 1 region
      position.x = 350;
      position.y = 40;
      return position;
    default:
      break;
  }
};

const getQlEditorByLayout = (layout, textEditors, regionId) => {
  let pos = 0;
  // Text left - Text right
  if (layout.id === 2) {
    pos = regionId === 'Text Region 1' ? 0 : 1;
    return textEditors[pos];
  }
  return textEditors[pos];
};

const applyMatrix = (context, matrix) => {
  context.setTransform(
    matrix[0],
    matrix[1],
    matrix[3],
    matrix[4],
    matrix[6],
    matrix[7],
  );
};

const renderBackground = (background, context, width, height) => {
  // Render out the background
  if (background.opacity > 0) {
    context.save();

    const opacity = background.opacity || 100;

    context.globalAlpha = opacity / 100;
    context.fillStyle = background.color;

    context.fillRect(0, 0, width, height);

    context.restore();
  }
};
const matrix = mat3.identity(mat3.create());

const prepareTransform = (field, width, height) => {
  const mtx = mat3.identity(matrix);

  mat3.translate(mtx, mtx, [width / 2, height / 2]);

  mat3.translate(mtx, mtx, [field.content?.x || 0, field.content?.y || 0]);

  const horizontalCoefficient = field.isFlippedHorizontally ? -1 : 1;
  const verticalCoefficient = field.isFlippedVertically ? -1 : 1;

  const scale = field.content?.transform?.scale || 1;

  mat3.scale(mtx, mtx, [
    horizontalCoefficient * scale,
    verticalCoefficient * scale,
  ]);

  mat3.translate(mtx, mtx, [-width / 2, -height / 2]);

  return mtx;
};

const render = (context, image, region, layout, isText) => {
  const canvas = document.createElement('canvas');

  canvas.setAttribute('width', region.width);
  canvas.setAttribute('height', region.height);
  const ctx = canvas.getContext('2d');
  // Reset the identity to clear out the canvas
  applyMatrix(ctx, mat3.identity(matrix));
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (image && image.src) {
    // Perform transformation
    applyMatrix(ctx, prepareTransform(region, image.width, image.height));

    ctx.drawImage(image, 0, 0, image.width, image.height);
  }

  const position = isText
    ? getTextRegionPosition(region, layout)
    : { x: region.x, y: region.y };

  context.translate(position.x, position.y);

  renderBackground(region.bg, context, region.width, region.height);

  context.drawImage(canvas, 0, 0, canvas.width, canvas.height);
};

const drawCanvas = async (layout) => {
  const regionPromises = [];
  const canvas = document.getElementById('wb-slide-editor-canvas');
  const context = canvas.getContext('2d');

  Object.keys(layout.regions).forEach((key) => {
    if (layout.regions[key].type === 'image') {
      const imgPromise = new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          resolve(() => {
            render(context, image, layout.regions[key], layout);
          });
        };

        image.setAttribute('crossorigin', 'anonymous');
        image.src = layout.regions[key]?.content?.image
          ? layout.regions[key].content.image.file_uri
          : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      });

      regionPromises.push(imgPromise);
    } else if (layout.regions[key].type === 'text') {
      const textPromise = new Promise((resolve) => {
        const textEditors = document.querySelectorAll('.ql-editor');
        const textEditor = getQlEditorByLayout(layout, textEditors, key);

        toPng(textEditor).then((dataUrl) => {
          const image = new Image();
          image.onload = () => {
            resolve(() => {
              render(context, image, layout.regions[key], layout, true);
            });
          };
          image.setAttribute('crossorigin', 'anonymous');
          image.src = dataUrl;
        });
      });
      regionPromises.push(textPromise);
    }
  });
  const res = await Promise.all(regionPromises);

  res.forEach((render) => {
    render();
  });

  return canvas;
};

const parseDataUrl = (dataUrl) => {
  const match = dataUrl.match(DATA_URL_REGEX);

  if (!match) {
    return null;
  }

  return {
    data: match[3],
    mime: match[1],
    encoding: match[2],
  };
};

export const getCanvasImageEmbedded = async (layout) => {
  const canvas = await drawCanvas(layout);
  const canvasUrl = canvas.toDataURL('image/jpeg');

  return parseDataUrl(canvasUrl);
};

export const hasImageOverscaled = (layout) => {
  if (!layout.regions) {
    return false;
  }

  let hasOverscaled = false;
  let key;
  let content;
  for (key in layout.regions) {
    content = layout.regions[key].content;
    if (!content || !content.transform) {
      continue;
    }

    // If the content has a large scale and the content.image is in there
    if (content.transform.scale > 1 && content.image !== '') {
      hasOverscaled = true;
    }
  }

  return hasOverscaled;
};
